/**
 * NBDY launch-day picker.
 *
 * Single purchase path through one state machine:
 *   Free sample — pick a tile → CLAIM. POSTs that tile's product's default
 *   variant ($0).
 *
 * Layout state:
 *   idle      → cream backdrop, rail/peek shows placeholder
 *   selected  → per-scent bg image fades in, rail/peek populates, CLAIM enables
 *   claiming  → spinner on the clicked button via .is-loading
 *   claimed   → rail/peek morphs into the inline confirmation panel
 *   sold-out  → server-rendered fallback
 *
 * Each tile is fully self-describing — its `data-*` attributes carry the
 * variant id, product name, formatted price, bg image URL, tagline, and
 * notes pyramid. The picker just reads from the clicked tile and broadcasts
 * to every `[data-rail-*]` element (the desktop rail AND the mobile sheet
 * share the same markup, so JS updates both in lockstep).
 *
 * Mobile sheet: a `data-expanded` toggle on the host expands the peek bar
 * into a full bottom sheet. Tap the chevron, the backdrop, or Esc to close.
 * Body scroll locks while expanded.
 *
 * Background swap: two stacked layers (a/b) crossfade via opacity. JS
 * writes the next URL into the inactive layer, toggles which is `.is-active`,
 * and the CSS handles the timing (400ms reveal, 350ms switch, 250ms hide).
 */
class NbdyLaunchPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.nbdy-launch-day');
    if (!this.section) return;

    // Tiles + state.
    this.tiles = Array.from(this.querySelectorAll('[data-tile]'));
    this.selectedTile = null;
    this.busy = false;
    this.activeLayerKey = null; // 'a' | 'b' | null

    // First tile gets keyboard focus entry. Server-rendered tabindex is -1
    // on every tile (block file can't know which is first), so we promote
    // here. Subsequent arrow-key nav handles tabindex shuffling.
    if (this.tiles[0]) this.tiles[0].setAttribute('tabindex', '0');

    // Sold-out detection — moved from server (block.settings can't be
    // pre-read for theme blocks) to runtime. If there ARE tiles but none
    // is available, flip the section into sold-out state.
    if (this.tiles.length > 0) {
      const anyAvailable = this.tiles.some(
        (t) => t.dataset.variantAvailable === 'true',
      );
      if (!anyAvailable) this.section.dataset.state = 'sold-out';
    }

    // Background stage refs (live on the section, not the picker).
    this.bgLayerA = this.section.querySelector('[data-bg-layer="a"]');
    this.bgLayerB = this.section.querySelector('[data-bg-layer="b"]');
    this.bgOverlay = this.section.querySelector('[data-bg-overlay]');

    // Rail/peek/sheet refs — querySelectorAll because the same markup
    // appears in BOTH the desktop rail and the mobile sheet. Updating
    // one updates both.
    this.eyebrowEls = this.#all('[data-rail-eyebrow]');
    this.nameEls = this.#all('[data-rail-name]');
    this.taglineEls = this.#all('[data-rail-tagline]');
    this.noteTopEls = this.#all('[data-rail-note-top]');
    this.noteMiddleEls = this.#all('[data-rail-note-middle]');
    this.noteBaseEls = this.#all('[data-rail-note-base]');
    this.priceEls = this.#all('[data-rail-price]');
    this.shippingEls = this.#all('[data-rail-shipping]');

    // CTAs — sample claim only (one path, two copies: rail + sheet/peek).
    this.claimCtas = this.#all('[data-claim-cta]');
    this.ctaLabels = this.#all('[data-cta-label]');

    // Claimed-panel refs.
    this.claimedThumbs = this.#all('[data-claimed-thumb]');
    this.claimedNames = this.#all('[data-claimed-name]');
    this.claimedPriceEls = this.#all('[data-claimed-price]');
    this.claimedShippingEls = this.#all('[data-claimed-shipping]');

    this.errorLines = this.#all('[data-error-line]');

    // Mobile bar + sheet.
    this.mobileBar = this.querySelector('[data-mobile-bar]');
    this.sheet = this.querySelector('[data-sheet]');
    this.sheetBackdrop = this.querySelector('[data-sheet-backdrop]');
    this.sheetToggle = this.querySelector('[data-sheet-toggle]');
    this.sheetCloseBtn = this.querySelector('[data-sheet-close]');

    // Server-passed labels + display values.
    this.idleEyebrow = this.dataset.idleEyebrow || 'Pick a scent';
    this.selectedEyebrow = this.dataset.selectedEyebrow || 'Selected';
    this.labelIdle = this.dataset.ctaIdle || 'Pick a scent';
    this.labelReady = this.dataset.ctaReady || 'Claim';
    this.labelClaiming = this.dataset.ctaClaiming || 'Claiming…';

    this.sampleShipping = this.dataset.sampleShipping || '';

    this.replaceCart = this.dataset.replaceCart === 'true';

    // Always-on default bg image (section-level setting). When set, the bg
    // layer paints this URL on init and reverts to it on unpick (instead of
    // fading out to cream). When unset, bg behaves as before — fades to
    // cream when idle, scent image when picked.
    this.defaultBgImage = this.dataset.defaultBgImage || '';

    // Initial paint: idle placeholders for the rail/peek.
    this.#renderIdle();

    // Initial bg: paint the section's default background image if one is set.
    if (this.defaultBgImage) this.#swapBackground(this.defaultBgImage);

    // Listeners.
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    for (const cta of this.claimCtas) cta.addEventListener('click', this.#handleSampleClaim);

    if (this.sheetToggle) this.sheetToggle.addEventListener('click', this.#toggleSheet);
    if (this.sheetCloseBtn) this.sheetCloseBtn.addEventListener('click', this.#collapseSheet);
    if (this.sheetBackdrop) this.sheetBackdrop.addEventListener('click', this.#collapseSheet);
    document.addEventListener('keydown', this.#handleDocKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    for (const cta of this.claimCtas || []) cta.removeEventListener('click', this.#handleSampleClaim);
    if (this.sheetToggle) this.sheetToggle.removeEventListener('click', this.#toggleSheet);
    if (this.sheetCloseBtn) this.sheetCloseBtn.removeEventListener('click', this.#collapseSheet);
    if (this.sheetBackdrop) this.sheetBackdrop.removeEventListener('click', this.#collapseSheet);
    document.removeEventListener('keydown', this.#handleDocKeydown);
    // Defensive: unlock body scroll if the element is removed mid-sheet-open.
    document.body.style.overflow = '';
  }

  #all(selector) {
    return Array.from(this.querySelectorAll(selector));
  }

  /* ---------- Tile selection ---------- */

  #handleClick = (event) => {
    const tile = event.target.closest('[data-tile]');
    if (!tile || !this.contains(tile)) return;
    if (tile.dataset.variantAvailable !== 'true') return;
    if (this.busy) return;

    const isClaimed = this.section.dataset.state === 'claimed';
    const isSameTile = tile === this.selectedTile;

    // Re-clicking the SAME tile while merely selected (not yet claimed)
    // = unpick. While claimed, re-clicking the same tile is a no-op
    // (they already have it). Clicking a DIFFERENT tile switches the
    // selection; from claimed state this flips back to 'selected' and
    // re-renders for the new pick, so when the customer re-confirms,
    // the 1-per-cart hard limit (#removeExistingSamples) swaps the cart
    // item instead of stacking a second one.
    if (isSameTile && !isClaimed) this.#unselectTile();
    else if (!isSameTile) this.#selectTile(tile);
  };

  #handleKeydown = (event) => {
    const tile = event.target.closest('[data-tile]');
    if (!tile || !this.contains(tile)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (tile.dataset.variantAvailable !== 'true') return;
      const isClaimed = this.section.dataset.state === 'claimed';
      const isSameTile = tile === this.selectedTile;
      if (isSameTile && !isClaimed) this.#unselectTile();
      else if (!isSameTile) this.#selectTile(tile);
      return;
    }

    if (event.key.startsWith('Arrow')) {
      event.preventDefault();
      const idx = this.tiles.indexOf(tile);
      if (idx === -1) return;
      const dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      let next = idx;
      for (let i = 0; i < this.tiles.length; i++) {
        next = (next + dir + this.tiles.length) % this.tiles.length;
        if (this.tiles[next].dataset.variantAvailable === 'true') break;
      }
      this.tiles[next]?.focus();
    }
  };

  #selectTile(tile) {
    this.selectedTile = tile;

    for (const t of this.tiles) {
      const isSel = t === tile;
      t.classList.toggle('is-selected', isSel);
      t.setAttribute('aria-checked', isSel ? 'true' : 'false');
      t.setAttribute('tabindex', isSel ? '0' : '-1');
    }

    this.#renderSelected(tile);
    this.#swapBackground(tile.dataset.bgImage || '');

    this.section.dataset.state = 'selected';
    for (const cta of this.claimCtas) cta.disabled = false;
    this.#clearError();
  }


  #unselectTile() {
    this.selectedTile = null;

    for (const t of this.tiles) {
      t.classList.remove('is-selected');
      t.setAttribute('aria-checked', 'false');
      t.setAttribute('tabindex', t === this.tiles[0] ? '0' : '-1');
    }

    this.section.dataset.state = 'idle';
    this.#renderIdle();
    // On unpick: restore the default bg if one was set, otherwise fade to cream.
    if (this.defaultBgImage) this.#swapBackground(this.defaultBgImage);
    else this.#fadeBackgroundOut();
    for (const cta of this.claimCtas) cta.disabled = true;
    this.#clearError();
  }

  /* ---------- Rail/peek content rendering ---------- */

  #renderIdle() {
    this.#setAll(this.eyebrowEls, this.idleEyebrow);
    this.#setAll(this.nameEls, '');
    this.#setAll(this.taglineEls, '');
    this.#setAll(this.noteTopEls, '');
    this.#setAll(this.noteMiddleEls, '');
    this.#setAll(this.noteBaseEls, '');
    this.#setAll(this.priceEls, '');
    this.#setAll(this.shippingEls, '');
    for (const lbl of this.ctaLabels) lbl.textContent = this.labelIdle;
  }

  #renderSelected(tile) {
    this.#setAll(this.eyebrowEls, this.selectedEyebrow);
    this.#setAll(this.nameEls, tile.dataset.scentName || '');
    this.#setAll(this.taglineEls, tile.dataset.tagline || '');
    this.#setAll(this.noteTopEls, tile.dataset.noteTop || '');
    this.#setAll(this.noteMiddleEls, tile.dataset.noteMiddle || '');
    this.#setAll(this.noteBaseEls, tile.dataset.noteBase || '');
    // Price comes from the tile (per-product, since each scent is its own
    // product now). Shipping note is section-level (same for every sample).
    this.#setAll(this.priceEls, tile.dataset.price || '');
    this.#setAll(this.shippingEls, this.sampleShipping);
    for (const lbl of this.ctaLabels) lbl.textContent = this.labelReady;
  }

  #setAll(els, text) {
    for (const el of els) el.textContent = text;
  }

  /* ---------- Background image crossfade ---------- */

  #swapBackground(url) {
    if (!url) {
      // No image for this tile — fade out whatever's active.
      this.#fadeBackgroundOut();
      return;
    }

    // Pick the layer that's NOT currently active; that one gets the new
    // image and fades in, while the other fades out. Result: a clean
    // crossfade with no flash between the two product backgrounds.
    const nextKey = this.activeLayerKey === 'a' ? 'b' : 'a';
    const nextLayer = nextKey === 'a' ? this.bgLayerA : this.bgLayerB;
    const currentLayer = this.activeLayerKey === 'a' ? this.bgLayerA : this.activeLayerKey === 'b' ? this.bgLayerB : null;

    if (!nextLayer) return;

    nextLayer.style.backgroundImage = `url("${url}")`;
    nextLayer.classList.add('is-active');
    if (currentLayer) currentLayer.classList.remove('is-active');
    this.activeLayerKey = nextKey;
  }

  #fadeBackgroundOut() {
    if (this.bgLayerA) this.bgLayerA.classList.remove('is-active');
    if (this.bgLayerB) this.bgLayerB.classList.remove('is-active');
    this.activeLayerKey = null;
    // Clear the bg-image after the fade so we're not holding bytes.
    window.setTimeout(() => {
      if (this.bgLayerA && !this.bgLayerA.classList.contains('is-active')) {
        this.bgLayerA.style.backgroundImage = '';
      }
      if (this.bgLayerB && !this.bgLayerB.classList.contains('is-active')) {
        this.bgLayerB.style.backgroundImage = '';
      }
    }, 400);
  }

  /* ---------- Mobile sheet ---------- */

  #toggleSheet = (event) => {
    event?.preventDefault();
    const expanded = this.mobileBar?.dataset.expanded === 'true';
    if (expanded) this.#collapseSheet();
    else this.#expandSheet();
  };

  #expandSheet = () => {
    if (!this.mobileBar) return;
    this.mobileBar.dataset.expanded = 'true';
    if (this.sheetToggle) this.sheetToggle.setAttribute('aria-expanded', 'true');
    if (this.sheet) this.sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Move focus into the sheet for accessibility.
    window.setTimeout(() => {
      const firstFocusable = this.sheet?.querySelector(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }, 280);
  };

  #collapseSheet = (event) => {
    event?.preventDefault();
    if (!this.mobileBar) return;
    this.mobileBar.dataset.expanded = 'false';
    if (this.sheetToggle) this.sheetToggle.setAttribute('aria-expanded', 'false');
    if (this.sheet) this.sheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.sheetToggle?.focus();
  };

  #handleDocKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.mobileBar?.dataset.expanded === 'true') {
      event.preventDefault();
      this.#collapseSheet();
    }
  };

  /* ---------- Claim flow ---------- */

  #handleSampleClaim = (event) => {
    event.preventDefault();
    if (!this.selectedTile) return;

    // Mobile peek CTA: don't claim straight from the collapsed peek bar.
    // Expand the sheet first so the user sees the scent details (notes,
    // tagline, price) before committing. The CTA INSIDE the expanded
    // sheet (rendered from the same cart_html, no data-peek-cta) is what
    // actually triggers the cart add on the second tap.
    const isPeekCta = event.currentTarget?.matches('[data-peek-cta]');
    const sheetCollapsed = this.mobileBar?.dataset.expanded !== 'true';
    if (isPeekCta && sheetCollapsed) {
      this.#expandSheet();
      return;
    }

    const tile = this.selectedTile;
    const img = tile.querySelector('.nbdy-launch-day__tile-img');

    this.#claim({
      button: event.currentTarget,
      variantId: tile.dataset.variantId,
      props: { _nbdy_launch_offer: 'sample' },
      info: {
        name: tile.dataset.scentName || '',
        price: tile.dataset.price || '',
        shipping: this.sampleShipping,
        image: img?.currentSrc || img?.src || '',
      },
    });
  };

  async #claim({ button, variantId, props, info }) {
    if (this.busy) return;
    this.busy = true;

    // Swap clicked button's label + show spinner. Only the clicked button
    // gets .is-loading — the rail and the sheet/peek share data-claim-cta
    // but only one is interactive per viewport (media-query display).
    const labelEl = button?.querySelector('.nbdy-launch-day__cta-label');
    const originalLabel = labelEl?.textContent;
    if (labelEl) labelEl.textContent = this.labelClaiming;
    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    this.#clearError();

    try {
      // Hard limit: only 1 launch-day sample in the cart at any time. If the
      // user already claimed a scent (this session or a prior one), remove
      // that line item before adding the new pick — so claiming a second
      // scent REPLACES the first instead of stacking. `replaceCart` is the
      // heavier opt-in that nukes the entire cart (any items, not just
      // launch samples); the hard limit always runs regardless.
      if (this.replaceCart) await this.#clearCart();
      else await this.#removeExistingSamples();
      await this.#postCartAdd(variantId, props);
      this.#enterClaimedState(info);
    } catch (err) {
      this.#showError(err?.message || 'Could not complete that. Please try again.');
      if (labelEl && originalLabel != null) labelEl.textContent = originalLabel;
      if (button) button.disabled = false;
    } finally {
      button?.classList.remove('is-loading');
      this.busy = false;
    }
  }

  async #clearCart() {
    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
    } catch {
      /* best-effort */
    }
  }

  // Surgically remove only line items carrying the launch-day sample
  // marker — leaves other (non-launch) items in the customer's cart
  // intact. Best-effort: if the cart query or per-item zero fails we
  // silently proceed to the add, so worst case the user ends up with
  // two samples instead of being blocked entirely.
  async #removeExistingSamples() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const samples = (cart.items || []).filter(
        (it) => it.properties && it.properties._nbdy_launch_offer === 'sample',
      );
      for (const item of samples) {
        const body = new FormData();
        body.append('id', item.key);
        body.append('quantity', '0');
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
      }
    } catch {
      /* best-effort */
    }
  }

  async #postCartAdd(variantId, props) {
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    for (const [key, value] of Object.entries(props || {})) {
      body.append(`properties[${key}]`, value);
    }
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    if (!response.ok) {
      let detail = '';
      try {
        const data = await response.json();
        detail = data?.description || data?.message || '';
      } catch {
        /* ignore */
      }
      throw new Error(detail || 'This item could not be added.');
    }
    return response.json();
  }

  #enterClaimedState(info) {
    for (const el of this.claimedNames) el.textContent = info.name || '';
    for (const el of this.claimedPriceEls) el.textContent = info.price || '';
    for (const el of this.claimedShippingEls) el.textContent = info.shipping || '';
    for (const el of this.claimedThumbs) {
      el.style.backgroundImage = info.image ? `url("${info.image}")` : '';
    }
    this.section.dataset.state = 'claimed';

    // Mobile: keep the sheet OPEN (or open it if somehow collapsed) so
    // the claimed panel — the thumb + scent name + "Continue to checkout"
    // CTA — slides up into view inside the same sheet that just held the
    // info. CSS handles the graceful swap via opacity/transform on
    // .nbdy-launch-day__cart and __panel scoped to mobile.
    const isMobile = window.matchMedia('(max-width: 959.98px)').matches;
    if (isMobile && this.mobileBar?.dataset.expanded !== 'true') {
      this.#expandSheet();
    }
  }

  #showError(msg) {
    for (const el of this.errorLines) {
      el.textContent = msg;
      el.classList.add('is-visible');
    }
  }

  #clearError() {
    for (const el of this.errorLines) {
      el.textContent = '';
      el.classList.remove('is-visible');
    }
  }
}

if (!customElements.get('nbdy-launch-picker')) {
  customElements.define('nbdy-launch-picker', NbdyLaunchPicker);
}
